import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Tabs,
  Card,
  Row,
  Col,
  Select,
  Input,
  Button,
  Tag,
  Typography,
  Flex,
  Empty,
  Modal,
  Form,
  Progress,
  Statistic,
  Space,
  Popconfirm,
  Checkbox,
  message,
  Drawer,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  SendOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  MailOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "axios";
import useUrlFilters from "hooks/useUrlFilters";

dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const { TextArea } = Input;

// ========== SEND EMAIL TAB ==========
const SendEmailTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [subject, setSubject] = useState("");
  const [broadcastName, setBroadcastName] = useState("");
  const [includeLeads, setIncludeLeads] = useState(true);
  const [includeContacts, setIncludeContacts] = useState(true);
  const [, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [, setActiveBroadcast] = useState(null);
  const [progress, setProgress] = useState(null);
  const progressTimer = useRef(null);

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    return () => {
      if (progressTimer.current) clearInterval(progressTimer.current);
    };
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`/campaign/campaigns/?page_size=500`);
      setCampaigns(res.data?.results || []);
    } catch (err) {
      console.error("Error loading campaigns:", err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`/emails/templates/?page_size=500`);
      setTemplates(res.data?.results || []);
    } catch (err) {
      console.error("Error loading templates:", err);
    }
  };

  const fetchRecipients = async (campaignId) => {
    try {
      setLoading(true);
      const res = await axios.get(`/emails/broadcasts/campaign_recipients/?campaign=${campaignId}`);
      setRecipientInfo(res.data);
    } catch (err) {
      console.error("Error loading recipients:", err);
      setRecipientInfo(null);
    }
    setLoading(false);
  };

  const handleCampaignChange = (value) => {
    setSelectedCampaign(value);
    if (value) {
      fetchRecipients(value);
      const camp = campaigns.find((c) => c.id === value);
      if (camp) setBroadcastName(`${camp.campaign_name} - Email Broadcast`);
    } else {
      setRecipientInfo(null);
    }
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
    const tmpl = templates.find((t) => t.id === value);
    if (tmpl) {
      setSubject(tmpl.subject);
    }
  };

  const pollProgress = (broadcastId) => {
    if (progressTimer.current) clearInterval(progressTimer.current);
    progressTimer.current = setInterval(async () => {
      try {
        const res = await axios.get(`/emails/broadcasts/${broadcastId}/progress/`);
        setProgress(res.data);
        if (res.data.status === "COMPLETED" || res.data.status === "FAILED") {
          clearInterval(progressTimer.current);
          progressTimer.current = null;
          setSending(false);
          message.success(
            `Broadcast ${res.data.status.toLowerCase()}: ${res.data.sent_count} sent, ${res.data.failed_count} failed`
          );
        }
      } catch (err) {
        console.error("Error polling progress:", err);
      }
    }, 3000);
  };

  const handleSend = async () => {
    if (!selectedCampaign || !selectedTemplate || !subject || !broadcastName) {
      message.warning("Please fill all required fields");
      return;
    }

    setSending(true);
    try {
      // Create broadcast
      const createRes = await axios.post(`/emails/broadcasts/`, {
        name: broadcastName,
        campaign: selectedCampaign,
        template: selectedTemplate,
        subject: subject,
        include_leads: includeLeads,
        include_contacts: includeContacts,
      });

      const broadcastId = createRes.data.id;
      setActiveBroadcast(broadcastId);

      // Send broadcast
      const sendRes = await axios.post(`/emails/broadcasts/${broadcastId}/send/`);
      setProgress({
        status: "QUEUED",
        total_recipients: sendRes.data.total_recipients,
        sent_count: 0,
        failed_count: 0,
        pending_count: sendRes.data.total_recipients,
      });

      // Start polling
      pollProgress(broadcastId);
      message.info(`Sending to ${sendRes.data.total_recipients} recipients...`);
    } catch (err) {
      console.error("Error sending broadcast:", err);
      const errMsg = err.response?.data?.error || "Failed to send broadcast";
      message.error(errMsg);
      setSending(false);
    }
  };

  const handleReset = () => {
    setSelectedCampaign(null);
    setSelectedTemplate(null);
    setRecipientInfo(null);
    setSubject("");
    setBroadcastName("");
    setIncludeLeads(true);
    setIncludeContacts(true);
    setActiveBroadcast(null);
    setProgress(null);
    if (progressTimer.current) clearInterval(progressTimer.current);
  };

  const selectedTemplateObj = templates.find((t) => t.id === selectedTemplate);

  return (
    <div style={{ padding: "0 16px" }}>
      {/* Progress Card - shown when sending */}
      {progress && (
        <Card
          style={{ marginBottom: 16 }}
          title={
            <Space>
              <MailOutlined />
              <span>Broadcast Progress</span>
              <Tag color={
                progress.status === "COMPLETED" ? "green" :
                progress.status === "FAILED" ? "red" :
                progress.status === "SENDING" ? "blue" : "orange"
              }>
                {progress.status}
              </Tag>
            </Space>
          }
          extra={
            (progress.status === "COMPLETED" || progress.status === "FAILED") && (
              <Button onClick={handleReset}>New Broadcast</Button>
            )
          }
        >
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Statistic title="Total" value={progress.total_recipients} prefix={<TeamOutlined />} />
            </Col>
            <Col span={6}>
              <Statistic title="Sent" value={progress.sent_count} valueStyle={{ color: "#3f8600" }} prefix={<CheckCircleOutlined />} />
            </Col>
            <Col span={6}>
              <Statistic title="Failed" value={progress.failed_count} valueStyle={{ color: "#cf1322" }} prefix={<CloseCircleOutlined />} />
            </Col>
            <Col span={6}>
              <Statistic title="Pending" value={progress.pending_count} prefix={<ClockCircleOutlined />} />
            </Col>
          </Row>
          <Progress
            percent={
              progress.total_recipients > 0
                ? Math.round(((progress.sent_count + progress.failed_count) / progress.total_recipients) * 100)
                : 0
            }
            status={
              progress.status === "COMPLETED" ? "success" :
              progress.status === "FAILED" ? "exception" : "active"
            }
            strokeColor={progress.status === "SENDING" ? "#1677ff" : undefined}
          />
        </Card>
      )}

      {/* Send Form - hide when actively sending */}
      {!progress && (
        <Row gutter={16}>
          <Col span={14}>
            <Card title="Compose Email Broadcast">
              <Form layout="vertical">
                <Form.Item label="Broadcast Name" required>
                  <Input
                    value={broadcastName}
                    onChange={(e) => setBroadcastName(e.target.value)}
                    placeholder="Enter broadcast name"
                  />
                </Form.Item>

                <Form.Item label="Campaign" required>
                  <Select
                    showSearch
                    placeholder="Select a campaign"
                    value={selectedCampaign}
                    onChange={handleCampaignChange}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={campaigns.map((c) => ({
                      value: c.id,
                      label: `${c.campaign_name} (${c.status_display})`,
                    }))}
                    allowClear
                  />
                </Form.Item>

                {recipientInfo && (
                  <Card size="small" style={{ marginBottom: 16, background: "#f6ffed" }}>
                    <Flex justify="space-between" align="center">
                      <Space>
                        <TeamOutlined />
                        <Text strong>{recipientInfo.total} recipients with email</Text>
                      </Space>
                      <Space>
                        <Tag color="blue">{recipientInfo.leads_with_email} Leads</Tag>
                        <Tag color="green">{recipientInfo.contacts_with_email} Contacts</Tag>
                      </Space>
                    </Flex>
                  </Card>
                )}

                <Form.Item label="Include">
                  <Space>
                    <Checkbox checked={includeLeads} onChange={(e) => setIncludeLeads(e.target.checked)}>
                      Leads
                    </Checkbox>
                    <Checkbox checked={includeContacts} onChange={(e) => setIncludeContacts(e.target.checked)}>
                      Contacts
                    </Checkbox>
                  </Space>
                </Form.Item>

                <Form.Item label="Email Template" required>
                  <Select
                    showSearch
                    placeholder="Select a template"
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={templates.map((t) => ({
                      value: t.id,
                      label: t.name,
                    }))}
                    allowClear
                  />
                </Form.Item>

                <Form.Item label="Subject" required>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject line"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    size="large"
                    onClick={handleSend}
                    loading={sending}
                    disabled={!selectedCampaign || !selectedTemplate || !subject || !broadcastName}
                  >
                    Send Broadcast
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          {/* Template Preview */}
          <Col span={10}>
            <Card
              title="Template Preview"
              style={{ height: "100%" }}
              extra={selectedTemplateObj && <Tag color="blue">{selectedTemplateObj.name}</Tag>}
            >
              {selectedTemplateObj ? (
                <div>
                  <Text strong>Subject: </Text>
                  <Text>{subject || selectedTemplateObj.subject}</Text>
                  <div
                    style={{
                      marginTop: 16,
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      padding: 16,
                      maxHeight: 400,
                      overflow: "auto",
                      background: "#fff",
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedTemplateObj.html_content }}
                  />
                </div>
              ) : (
                <Empty description="Select a template to preview" />
              )}
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

// ========== TEMPLATES TAB ==========
const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [form] = Form.useForm();

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/emails/templates/?page_size=500`);
      setTemplates(res.data?.results || []);
    } catch (err) {
      console.error("Error loading templates:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingTemplate) {
        await axios.put(`/emails/templates/${editingTemplate.id}/`, values);
        message.success("Template updated");
      } else {
        await axios.post(`/emails/templates/`, values);
        message.success("Template created");
      }
      setModalOpen(false);
      form.resetFields();
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      console.error("Error saving template:", err);
      message.error("Failed to save template");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/emails/templates/${id}/`);
      message.success("Template deleted");
      fetchTemplates();
    } catch (err) {
      message.error("Failed to delete template");
    }
  };

  const handleEdit = (record) => {
    setEditingTemplate(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    form.resetFields();
    setModalOpen(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm"),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewTemplate(record)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete this template?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "0 16px" }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>Email Templates</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchTemplates} loading={loading} />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            New Template
          </Button>
        </Space>
      </Flex>

      <Table
        dataSource={templates}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />

      {/* Create/Edit Modal */}
      <Modal
        title={editingTemplate ? "Edit Template" : "Create Template"}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => {
          setModalOpen(false);
          setEditingTemplate(null);
          form.resetFields();
        }}
        width={800}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Template Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Welcome Email, Product Launch" />
          </Form.Item>
          <Form.Item name="subject" label="Subject Line" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="Email subject line" />
          </Form.Item>
          <Form.Item
            name="html_content"
            label="HTML Content"
            rules={[{ required: true, message: "Required" }]}
          >
            <TextArea rows={12} placeholder="<html>...</html>" style={{ fontFamily: "monospace" }} />
          </Form.Item>
          <Form.Item name="plain_text_content" label="Plain Text (fallback)">
            <TextArea rows={4} placeholder="Plain text version for email clients that don't support HTML" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Drawer */}
      <Drawer
        title={previewTemplate?.name || "Template Preview"}
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        width={640}
      >
        {previewTemplate && (
          <div>
            <Text strong>Subject: </Text>
            <Text>{previewTemplate.subject}</Text>
            <div
              style={{
                marginTop: 16,
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: 16,
                background: "#fff",
              }}
              dangerouslySetInnerHTML={{ __html: previewTemplate.html_content }}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

// ========== HISTORY TAB ==========
// The status column filter and page live in the URL so the view can be shared as a link.
const HISTORY_URL_FILTERS = {
  status: { type: "array" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 20 },
};

const HistoryTab = () => {
  const [filters, setFilters] = useUrlFilters(HISTORY_URL_FILTERS);
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const fetchBroadcasts = useCallback(async () => {
    setLoading(true);
    try {
      // Paged client-side, so load up to the largest page size on offer.
      const res = await axios.get(`/emails/broadcasts/?page_size=${MAX_PAGE_SIZE}`);
      setBroadcasts(res.data?.results || []);
    } catch (err) {
      console.error("Error loading broadcasts:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);

  const fetchRecipients = async (broadcastId) => {
    setRecipientsLoading(true);
    try {
      const res = await axios.get(`/emails/broadcasts/${broadcastId}/recipients/?page_size=${MAX_PAGE_SIZE}`);
      setRecipients(res.data?.results || []);
    } catch (err) {
      console.error("Error loading recipients:", err);
    }
    setRecipientsLoading(false);
  };

  const handleViewRecipients = (record) => {
    setSelectedBroadcast(record);
    fetchRecipients(record.id);
  };

  const statusColors = {
    DRAFT: "default",
    QUEUED: "orange",
    SENDING: "processing",
    COMPLETED: "success",
    FAILED: "error",
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Campaign",
      dataIndex: "campaign_name",
      key: "campaign_name",
      ellipsis: true,
    },
    {
      title: "Template",
      dataIndex: "template_name",
      key: "template_name",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => <Tag color={statusColors[text]}>{text}</Tag>,
      filters: [
        { text: "Draft", value: "DRAFT" },
        { text: "Queued", value: "QUEUED" },
        { text: "Sending", value: "SENDING" },
        { text: "Completed", value: "COMPLETED" },
        { text: "Failed", value: "FAILED" },
      ],
      filteredValue: filters.status,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Total",
      dataIndex: "total_recipients",
      key: "total_recipients",
      width: 80,
    },
    {
      title: "Sent",
      dataIndex: "sent_count",
      key: "sent_count",
      width: 80,
      render: (text) => <Text style={{ color: "#3f8600" }}>{text}</Text>,
    },
    {
      title: "Failed",
      dataIndex: "failed_count",
      key: "failed_count",
      width: 80,
      render: (text) => <Text style={{ color: text > 0 ? "#cf1322" : undefined }}>{text}</Text>,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm"),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: "descend",
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewRecipients(record)}>
          Details
        </Button>
      ),
    },
  ];

  const recipientColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Type",
      dataIndex: "recipient_type",
      key: "recipient_type",
      render: (text) => <Tag color={text === "lead" ? "blue" : "green"}>{text}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Tag color={text === "SENT" ? "success" : text === "FAILED" ? "error" : "default"}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Sent At",
      dataIndex: "sent_at",
      key: "sent_at",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY HH:mm:ss") : "-"),
    },
    {
      title: "Error",
      dataIndex: "error_message",
      key: "error_message",
      ellipsis: true,
      render: (text) => text || "-",
    },
  ];

  return (
    <div style={{ padding: "0 16px" }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>Broadcast History</Title>
        <Button icon={<ReloadOutlined />} onClick={fetchBroadcasts} loading={loading} />
      </Flex>

      <Table
        dataSource={broadcasts}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize, current: filters.page, showSizeChanger: true }}
        onChange={(pagination, tableFilters) =>
          setFilters({
            page: pagination.pageSize !== pageSize ? 1 : pagination.current,
            pageSize: pagination.pageSize,
            status: tableFilters.status || [],
          })
        }
      />

      {/* Recipients Drawer */}
      <Drawer
        title={
          selectedBroadcast ? (
            <Space>
              <span>{selectedBroadcast.name}</span>
              <Tag color={statusColors[selectedBroadcast.status]}>{selectedBroadcast.status}</Tag>
            </Space>
          ) : "Recipients"
        }
        open={!!selectedBroadcast}
        onClose={() => {
          setSelectedBroadcast(null);
          setRecipients([]);
        }}
        width={800}
      >
        {selectedBroadcast && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic title="Total" value={selectedBroadcast.total_recipients} />
              </Col>
              <Col span={8}>
                <Statistic title="Sent" value={selectedBroadcast.sent_count} valueStyle={{ color: "#3f8600" }} />
              </Col>
              <Col span={8}>
                <Statistic title="Failed" value={selectedBroadcast.failed_count} valueStyle={{ color: "#cf1322" }} />
              </Col>
            </Row>
            <Table
              dataSource={recipients}
              columns={recipientColumns}
              rowKey="id"
              loading={recipientsLoading}
              size="small"
              pagination={{ defaultPageSize: 50, showSizeChanger: true }}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

// ========== MAIN PAGE ==========
const TAB_URL_FILTERS = {
  tab: { type: "string", default: "send" },
};

const Emails = () => {
  const [tabFilters, setTabFilters] = useUrlFilters(TAB_URL_FILTERS);
  const activeTab = tabFilters.tab;
  const setActiveTab = (tab) => setTabFilters({ tab }, { replace: false });

  const tabItems = [
    {
      key: "send",
      label: (
        <span>
          <SendOutlined /> Send Email
        </span>
      ),
      children: <SendEmailTab />,
    },
    {
      key: "templates",
      label: (
        <span>
          <MailOutlined /> Templates
        </span>
      ),
      children: <TemplatesTab />,
    },
    {
      key: "history",
      label: (
        <span>
          <HistoryOutlined /> History
        </span>
      ),
      children: <HistoryTab />,
    },
  ];

  return (
    <div
      className="custom-scroll-div"
      style={{ overflowY: "scroll", maxHeight: "90vh", padding: 10 }}
    >
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <Title level={3} style={{ margin: 0 }}>
          <MailOutlined style={{ marginRight: 8 }} />
          Email Broadcast
        </Title>
      </Flex>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        style={{ marginTop: 8 }}
      />
    </div>
  );
};

export default Emails;
